import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.fetchUserData()
  }

  async fetchUserData() {
    try {
      const response = await fetch('/api/user/watch_data')
      const data = await response.json()
      
      if (data.authenticated) {
        this.updateWatchButtons(data.watched_items)
        this.updateCollectionButtons(data.collections)
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  updateWatchButtons(watchedItems) {
    // Find all watch-related forms
    document.querySelectorAll('form.button_to').forEach(form => {
      // Check if this is a watch form by looking at the URL pattern
      const isWatchForm = form.action.includes('/watch') || form.action.includes('/unwatch');
      
      if (!isWatchForm) {
        return;
      }
      
      // Extract the watchable ID and type from the URL
      let urlParts = form.action.split('/');
      let watchableId, watchableType;
      
      // /clinicaltrials/:id/watch or /clinicaltrials/:id/unwatch
      if (form.action.includes('/clinicaltrials/')) {
        watchableId = urlParts[urlParts.indexOf('clinicaltrials') + 1];
        watchableType = 'ClinicalTrial';
      } else {
        // Skip forms we can't identify
        return;
      }
      
      // Check if this item is in the user's watched items
      const isWatched = watchedItems[watchableType.toLowerCase()] && 
                       watchedItems[watchableType.toLowerCase()].includes(watchableId);
      
      // Get the button and icon
      const button = form.querySelector('button');
      const icon = button?.querySelector('i');
      
      if (!button || !icon) {
        return;
      }
      
      if (isWatched) {
        // Update to watched state (solid star)
        if (!icon.classList.contains('fa-solid')) {
          // Update button
          button.classList.remove('btn-warning', 'text-dark');
          button.classList.add('btn-primary', 'border-warning', 'text-warning');
          button.title = 'Unwatch';
          
          // Update icon
          icon.classList.remove('fa-regular');
          icon.classList.add('fa-solid');
          
          // Update form action to unwatch
          if (!form.action.includes('/unwatch')) {
            form.action = form.action.replace('/watch', '/unwatch');
            
            // Add the delete method if needed
            let methodInput = form.querySelector('input[name="_method"]');
            if (!methodInput) {
              methodInput = document.createElement('input');
              methodInput.type = 'hidden';
              methodInput.name = '_method';
              methodInput.value = 'delete';
              methodInput.autocomplete = 'off';
              form.insertBefore(methodInput, form.firstChild);
            }
          }
        }
      } else if (icon.classList.contains('fa-solid')) {
        // Update to unwatched state (regular star)
        // Update button
        button.classList.remove('btn-primary', 'border-warning', 'text-warning');
        button.classList.add('btn-warning', 'text-dark');
        button.title = 'Watch';
        
        // Update icon
        icon.classList.remove('fa-solid');
        icon.classList.add('fa-regular');
        
        // Update form action to watch
        if (!form.action.includes('/watch')) {
          form.action = form.action.replace('/unwatch', '/watch');
          
          // Remove any _method input that might be present
          const methodInput = form.querySelector('input[name="_method"]');
          if (methodInput) {
            form.removeChild(methodInput);
          }
        }
      }
    });
  }
  
  updateCollectionButtons(collections) {
    // Work directly with the forms based on the DOM structure
    document.querySelectorAll('form.button_to').forEach(form => {
      // Extract URL from the form action or hidden input
      let requestPathInput = form.querySelector('input[name="request_path"]');
      let url = requestPathInput?.value;
      
      // Skip forms that don't appear to be collection forms
      if (!url && !form.action.includes('/collections')) {
        return;
      }
      
      // Get the current path (without hostname)
      const currentPath = window.location.pathname;
      
      const matchingCollection = collections.find(collection => {
        if (!collection.url) return false;
        
        // Direct match
        if (collection.url === url) {
          return true;
        }
        
        // Check if the collection's URL matches the current path
        if (collection.url === currentPath) {
          return true;
        }
        
        // Check if this form already points to this collection (for forms that are already in "collected" state)
        if (form.action.includes(`/collections/${collection.id}?`)) {
          return true;
        }
        
        return false;
      });
      const isCollected = !!matchingCollection;
      
      // Get the button and icon
      const button = form.querySelector('button');
      const icon = button?.querySelector('i');
      
      if (!button || !icon) {
        return;
      }
      
      if (isCollected) {
        // Update to collected state (fa-eye-slash)
        button.classList.remove('btn-info', 'text-white');
        button.classList.add('btn-white', 'border-info');
        button.title = 'Remove from collections';
        
        // Update icon
        if (!icon.classList.contains('fa-eye-slash')) {
          icon.classList.remove('fa-eye');
          icon.classList.add('fa-eye-slash', 'text-info');
        }
        
        // Make sure form action points to the right collection
        if (matchingCollection && !form.action.includes(`/collections/${matchingCollection.id}`)) {
          const newAction = `/collections/${matchingCollection.id}?return_path=${encodeURIComponent(window.location.pathname)}`;
          form.action = newAction;
          
          // Add the delete method if needed
          let methodInput = form.querySelector('input[name="_method"]');
          if (!methodInput) {
            methodInput = document.createElement('input');
            methodInput.type = 'hidden';
            methodInput.name = '_method';
            methodInput.value = 'delete';
            methodInput.autocomplete = 'off';
            form.insertBefore(methodInput, form.firstChild);
          }
        }
      } else if (icon.classList.contains('fa-eye-slash')) {
        // Only update if it's actually a collection form that needs to be changed
        // Update to uncollected state (fa-eye)
        button.classList.remove('btn-white', 'border-info');
        button.classList.add('btn-info', 'text-white');
        button.title = 'Add to collections';
        
        // Update icon
        icon.classList.remove('fa-eye-slash', 'text-info');
        icon.classList.add('fa-eye');
        
        // Update form action to collections path
        form.action = '/collections';
        
        // Remove any _method input that might be present
        const methodInput = form.querySelector('input[name="_method"]');
        if (methodInput) {
          form.removeChild(methodInput);
        }
      }
    });
  }
}